<template>
  <!-- S01 · Resumen: seguimiento, fechas/recursos, Top 3 y próximos pasos. -->
  <div class="s01">
    <!-- Seguimiento del proyecto -->
    <summary-card :title="$t('prm_summary_tracking')" :mode="mode">
      <template #extra>
        <span class="report-date">{{ $t('prm_summary_report_date') }}:
          <manual-field :value="s01.reportDate" type="date" @save="save('s01.reportDate', $event)"></manual-field></span>
      </template>
      <div class="kv-grid">
        <div class="kv-col">
          <!-- SAP / Nº proyecto: manual, se edita en Datos de contrato (S02). Solo lectura aquí;
               si no se ha rellenado, cae a la referencia real del proyecto. -->
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_sap') }}</span>
            <span class="val"><b>{{ s01.sap || sources.sapCode || '-' }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_project_name') }}</span>
            <span class="val"><b>{{ sources.projectName || '-' }}</b><source-chip v-if="mode" source="admin"></source-chip></span></div>
          <!-- Cliente: automático, se toma de Datos de contrato (S02). Solo lectura aquí. -->
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_client') }}</span>
            <span class="val"><b>{{ s01.client || '-' }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_rag') }}</span>
            <span class="val"><span class="rag-dot" :class="rag"></span><b class="rag-txt" :class="rag">{{ $t('prm_summary_rag_' + rag) }}</b><source-chip v-if="mode" source="calc" :tip-override="$t('prm_summary_rag_tip')"></source-chip></span></div>
          <!-- Avance: el mismo "Progreso total proyecto" de la vista de grupos (progreso actual). -->
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_progress') }}</span>
            <span class="val"><b>{{ hasProgress ? progress.current.toFixed(1) + ' %' : '-' }}</b><source-chip v-if="mode" source="medic"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_current_margin') }}</span>
            <span class="val"><b>{{ prm ? pct(prm.actualMargin) : '-' }}</b><source-chip v-if="mode" source="prm"></source-chip></span></div>
          <!-- Margen comercial: mismo dato que el Control económico (margen % del encaje comercial). -->
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_commercial_margin') }}</span>
            <span class="val"><b>{{ prm ? pct(prm.commercialMargin) : '-' }}</b><source-chip v-if="mode" source="prm"></source-chip></span></div>
        </div>
        <div class="kv-col">
          <div class="kv" :class="{ sourced: mode }" v-for="f in invoicingFields" :key="f.key"><span class="lbl">{{ $t(f.label) }}</span>
            <span class="val"><b>{{ eur(billing[f.key]) }}</b><source-chip v-if="mode" :source="f.src"></source-chip></span></div>
          <!-- Órdenes de cambio confirmadas: fuente Control económico (Plantilla PRM). -->
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_change_orders') }}</span>
            <span class="val"><b>{{ prm ? eur(prm.changeOrders) : '-' }}</b><source-chip v-if="mode" source="prm"></source-chip></span></div>
        </div>
      </div>
    </summary-card>

    <!-- Fechas clave del proyecto (solo lectura; se editan en Datos de contrato). -->
    <summary-card :title="$t('prm_summary_kd_project')" :mode="mode">
      <div class="kv-grid">
        <div class="kv-col">
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_contract_start') }}</span>
            <span class="val"><b>{{ kdDate('contractStart') }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_contract_end') }}</span>
            <span class="val"><b>{{ kdDate('contractEnd') }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_signature') }}</span>
            <span class="val"><b>{{ kdDate('contractSignature') }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
        </div>
        <div class="kv-col">
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_real_start') }}</span>
            <span class="val"><b>{{ autoDate('realStart') }}</b><source-chip v-if="mode" source="planif"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_real_end') }}</span>
            <span class="val"><b>{{ autoDate('realEnd') }}</b><source-chip v-if="mode" source="planif"></source-chip></span></div>
        </div>
      </div>
    </summary-card>

    <!-- Fechas clave de la instalación (Field): previstas manuales (S02) + reales de Planificación. -->
    <summary-card :title="$t('prm_summary_kd_installation')" :subtitle="$t('prm_summary_kd_installation_note')" :mode="mode">
      <div class="kv-grid">
        <div class="kv-col">
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_install_planned_start') }}</span>
            <span class="val"><b>{{ kdDate('installPlannedStart') }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_install_planned_end') }}</span>
            <span class="val"><b>{{ kdDate('installPlannedEnd') }}</b><source-chip v-if="mode" source="contract" :tip-override="$t('prm_summary_src_from_contract')"></source-chip></span></div>
        </div>
        <div class="kv-col">
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_install_real_start') }}</span>
            <span class="val"><b>{{ autoDate('installRealStart') }}</b><source-chip v-if="mode" source="planif"></source-chip></span></div>
          <div class="kv" :class="{ sourced: mode }"><span class="lbl">{{ $t('prm_summary_kd_install_real_end') }}</span>
            <span class="val"><b>{{ autoDate('installRealEnd') }}</b><source-chip v-if="mode" source="planif"></source-chip></span></div>
        </div>
      </div>
    </summary-card>

    <!-- Riesgos y oportunidades críticas: derivadas de Riesgos y Oportunidades (I x P >= 12, no cerradas). Solo lectura. -->
    <summary-card :title="$t('prm_summary_critical_risks')" :subtitle="$t('prm_summary_critical_note')" :mode="mode" chip="resumen">
      <div class="crit-wrap">
        <table class="crit-table">
          <thead>
            <tr>
              <th>{{ $t('prm_summary_col_type') }}</th>
              <th class="desc-col">{{ $t('prm_summary_col_description') }}</th>
              <th>{{ $t('prm_summary_col_owner') }}</th>
              <th>{{ $t('prm_summary_col_target_date') }}</th>
              <th>{{ $t('prm_summary_col_status') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in criticalRisks" :key="'cr' + i">
              <td><span :class="r.type === 'opportunity' ? 'type-opportunity' : 'type-threat'">{{ riskType(r.type) }}</span></td>
              <td class="desc-col">{{ r.description || '-' }}</td>
              <td>{{ r.owner || '-' }}</td>
              <td>{{ r.targetDate ? fmtDate(r.targetDate) : '-' }}</td>
              <td><span class="badge" :class="'st-' + (r.status || 'open')">{{ riskStatus(r.status) }}</span></td>
            </tr>
            <tr v-if="!criticalRisks.length"><td class="empty" colspan="5">{{ $t('prm_summary_no_critical') }}</td></tr>
          </tbody>
        </table>
      </div>
    </summary-card>

    <!-- Proximos pasos: descripcion + responsable + fecha prevista (sin fuente). -->
    <summary-card :title="$t('prm_summary_next_steps')" :mode="mode" :mark="true" chip="none">
      <template #extra><span class="max-tag">{{ $t('prm_summary_max_5') }}</span></template>
      <prm-summary-editable-table
        :rows="s01.nextSteps || []" :columns="stepColumns" :blank="blankStep" :max-rows="5" :mark="mode" :numbered="true"
        :add-label="$t('prm_summary_add_row')" :remove-title="$t('prm_summary_remove_row')"
        :empty-text="$t('prm_summary_empty')" :limit-hint="$t('prm_summary_max_5')" :reset-key="resetKey"
        @change="(items) => $emit('save-list', { path: 's01.nextSteps', items })">
      </prm-summary-editable-table>
    </summary-card>
  </div>
</template>

<script>
  import SummaryCard from './summary-card.vue'
  import PrmSummaryEditableTable from './editable-table.vue'
  import SourceChip from './source-chip.vue'
  import ManualField from './manual-field.vue'
  import { fmtEur, fmtPct, fmtDate } from '../shared/format'
  import { riskScore, userOptions } from './summary-model'
  import { SIGNIFICANT_RISK } from '../dashboard/dashboard-model'

  export default {
    name: 'PrmSummaryS01',

    components: { SummaryCard, PrmSummaryEditableTable, SourceChip, ManualField },

    props: {
      s01: { type: Object, required: true },
      sources: { type: Object, required: true },
      keyDates: { type: Object, default: () => ({}) }, // fechas manuales del contrato (doc.s02.keyDates), solo lectura aquí
      billing: { type: Object, default: () => ({}) }, // facturación derivada de S03 (solo lectura)
      risks: { type: Array, default: () => [] }, // riesgos S05 para las riesgos/oportunidades críticas
      rag: { type: String, default: 'green' },
      mode: { type: Boolean, default: false }, // modo "Fuente de datos"
      resetKey: { type: Number, default: 0 },
    },

    computed: {
      prm () { return this.sources.prm },
      progress () { return this.sources.progress || {} },
      // Fechas reales (inicio/fin del proyecto e instalación) derivadas de las actividades.
      autoDates () { return this.sources.keyDates || {} },
      hasProgress () { return this.progress && this.progress.current != null },
      // Facturación de cliente: derivada de los hitos de S03 (solo lectura). La pastilla refleja
      // qué es el dato (facturación / contractual); el tooltip aclara que se calcula desde S03.
      invoicingFields () {
        return [
          { key: 'invoiced', label: 'prm_summary_invoiced', src: 'fact' },
          { key: 'pendingToInvoice', label: 'prm_summary_pending_invoice', src: 'fact' },
          { key: 'collected', label: 'prm_summary_collected', src: 'fact' },
          { key: 'invoicedNotCollected', label: 'prm_summary_invoiced_not_collected', src: 'fact' },
          { key: 'invoicedOverdue', label: 'prm_summary_invoiced_overdue', src: 'fact' },
          { key: 'penaltyTotal', label: 'prm_summary_penalty_total', src: 'contract' },
        ]
      },
      // Responsable: usuarios del proyecto (fuente real, de sources).
      ownerOptions () { return userOptions(this.sources.users) },
      // Riesgos y oportunidades críticas: de S05, no cerradas y con criticidad (I x P) >= 12, mayor primero.
      criticalRisks () {
        return [...(this.risks || [])]
          .filter((r) => r.status !== 'closed' && riskScore(r) >= SIGNIFICANT_RISK)
          .sort((a, b) => riskScore(b) - riskScore(a))
      },
      stepColumns () {
        return [
          { key: 'text', label: this.$t('prm_summary_col_description'), type: 'text', width: '55%' },
          { key: 'owner', label: this.$t('prm_summary_col_owner'), type: 'select', align: 'center', options: this.ownerOptions },
          { key: 'plannedDate', label: this.$t('prm_summary_col_planned_date'), type: 'date', align: 'center' },
        ]
      },
      blankStep () {
        return { text: '', owner: '', plannedDate: '' }
      },
    },

    methods: {
      pct (fraction) { return fmtPct(fraction) },
      eur (v) { return fmtEur(v) },
      fmtDate (v) { return fmtDate(v) },
      // Fecha manual del contrato (desde S02) formateada, o rayita.
      kdDate (field) { return this.keyDates[field] ? fmtDate(this.keyDates[field]) : '—' },
      // Fecha real derivada de las actividades, o rayita.
      autoDate (field) { return this.autoDates[field] ? fmtDate(this.autoDates[field]) : '—' },
      riskType (code) { return this.$t('prm_summary_risk_type_' + (code || 'threat')) },
      riskStatus (code) { return this.$t('prm_summary_risk_status_' + (code || 'open')) },
      save (path, value) {
        this.$emit('save-field', { path, value })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .s01 { display: flex; flex-direction: column; gap: 10px; }
  .report-date { margin-left: auto; font-size: 10px; font-weight: 400; text-transform: none; color: #9aa4b2; }
  .max-tag { font-size: 9px; font-weight: 400; text-transform: none; color: #9aa4b2; }

  .kv-grid { display: grid; grid-template-columns: 1fr 1fr; }
  .kv-col:nth-child(2) { border-left: 1px solid #dfe4ea; }
  .kv { display: flex; align-items: center; border-bottom: 1px solid #eef0f3; min-height: 25px; }
  .kv .lbl {
    width: 180px; flex-shrink: 0; padding: 4px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .02em; color: #5a7896; background: #f6f8fa; align-self: stretch; display: flex; align-items: center;
  }
  .kv .val { flex: 1; display: flex; align-items: center; gap: 2px; padding: 3px 10px; font-size: 11px; color: #2c3a4d; min-width: 0; }
  .kv .val b { font-weight: 600; }
  // Fila sin fuente (manual) resaltada en ámbar (toda la fila) cuando el modo está activo.
  .kv.manual .lbl, .kv.manual .val { background: #fff4d6; }
  .kv.manual .lbl { color: #8a6d00; }
  // Fila con fuente real: tinte azul suave en modo fuente (evita huecos blancos).
  .kv.sourced .lbl, .kv.sourced .val { background: #eef4fb; }

  .rag-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; margin-right: 4px; }
  .rag-dot.green { background: #27ae60; } .rag-dot.amber { background: #f0a500; } .rag-dot.red { background: #e53e3e; }
  .rag-txt.green { color: #1e8449; } .rag-txt.amber { color: #b9770e; } .rag-txt.red { color: #c0392b; }

  // Riesgos y oportunidades críticas (solo lectura, derivada de S05): misma familia que Revisión contractual.
  .crit-wrap { overflow-x: auto; }
  .crit-table { width: 100%; border-collapse: collapse; }
  .crit-table th {
    background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .03em;
    font-weight: 700; padding: 5px 8px; text-align: left; white-space: nowrap;
  }
  .crit-table td { border-bottom: 1px solid #eef0f3; padding: 4px 8px; font-size: 11px; color: #2c3a4d; vertical-align: middle; white-space: nowrap; }
  .crit-table th.desc-col, .crit-table td.desc-col { white-space: normal; width: 40%; }
  .crit-table td.empty { text-align: center; color: #9aa4b2; padding: 12px; }
  .crit-table tbody tr:nth-child(even) td { background: #f9fafb; }
  .crit-table tbody tr:hover td { background: #eef2f7; }
  // Tipo: riesgo (rojo) / oportunidad (verde).
  .crit-table .type-threat { color: #c0392b; font-weight: 700; text-transform: uppercase; }
  .crit-table .type-opportunity { color: #1e8449; font-weight: 700; text-transform: uppercase; }
  // Badge de estado.
  .crit-table .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; }
  .crit-table .st-open { background: #fdecea; color: #c0392b; }
  .crit-table .st-in_progress { background: #fdf3d7; color: #b7791f; }
  .crit-table .st-closed { background: #e7f6ec; color: #1e8449; }
</style>
